import { useEffect, useState } from "react";
import axios from "axios";
import { Download, CheckCircle2, RotateCcw, Inbox, Briefcase } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";

const when = (d) => { try { return new Date(d).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return d; } };

const Ticket = ({ t, onToggle }) => (
  <div data-testid={`admin-ticket-${t.ticket_number}`} className="rounded-xl border border-gold-500/12 p-4">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="font-ui text-sm">
        <span className="text-gold-200 font-semibold">{t.ticket_number}</span>
        <span className="text-slate-500"> · {when(t.created_at)} · {t.category}</span>
        <span className={`ml-2 rounded-full px-2 py-0.5 text-xs ${t.status === "open" ? "bg-amber-400/15 text-amber-200" : "bg-emerald-400/15 text-emerald-200"}`}>{t.status}</span>
      </div>
      <button data-testid={`admin-ticket-toggle-${t.ticket_number}`} onClick={() => onToggle(t)} className="inline-flex items-center gap-1.5 font-ui text-xs text-slate-300 border border-gold-500/20 rounded-full px-3 py-1.5 hover:border-gold-300/60 transition-colors">
        {t.status === "open" ? <><CheckCircle2 className="w-3.5 h-3.5" /> Close</> : <><RotateCcw className="w-3.5 h-3.5" /> Reopen</>}
      </button>
    </div>
    <h4 className="mt-2 font-ui text-sm font-semibold text-slate-100">{t.subject}</h4>
    <p className="font-ui text-xs text-slate-400">{t.name} · <a href={`mailto:${t.email}?subject=Re: ${encodeURIComponent(`[${t.ticket_number}] ${t.subject}`)}`} className="text-gold-200 hover:underline">{t.email}</a>{t.book_id ? ` · book ${t.book_id}` : ""}</p>
    <p className="mt-2 font-read text-sm text-slate-300 whitespace-pre-wrap">{t.message}</p>
  </div>
);

const Application = ({ a }) => {
  const download = async () => {
    try {
      const r = await axios.get(`${API}/admin/careers/applications/${a.id}/resume`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const link = Object.assign(document.createElement("a"), { href: url, download: a.resume.filename });
      link.click(); setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch { toast.error("Could not download that resume."); }
  };
  return (
    <div data-testid={`admin-application-${a.reference}`} className="rounded-xl border border-gold-500/12 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="font-ui text-sm"><span className="text-gold-200 font-semibold">{a.role_label}</span><span className="text-slate-500"> · {a.reference} · {when(a.created_at)}</span></div>
        <button data-testid={`admin-application-download-${a.reference}`} onClick={download} className="inline-flex items-center gap-1.5 font-ui text-xs text-slate-300 border border-gold-500/20 rounded-full px-3 py-1.5 hover:border-gold-300/60 transition-colors">
          <Download className="w-3.5 h-3.5" /> {a.resume.filename} ({Math.ceil(a.resume.size / 1024)} KB)
        </button>
      </div>
      <p className="mt-2 font-ui text-sm text-slate-100">{a.name} · <a href={`mailto:${a.email}`} className="text-gold-200 hover:underline">{a.email}</a></p>
      {a.portfolio_url && <a href={a.portfolio_url} target="_blank" rel="noopener noreferrer nofollow" className="font-ui text-xs text-slate-400 hover:text-gold-200 break-all">{a.portfolio_url}</a>}
      {a.message && <p className="mt-2 font-read text-sm text-slate-300 whitespace-pre-wrap">{a.message}</p>}
    </div>
  );
};

export const AdminInbox = () => {
  const [tickets, setTickets] = useState([]);
  const [apps, setApps] = useState([]);
  const load = () => {
    axios.get(`${API}/admin/support/tickets`).then((r) => setTickets(r.data)).catch(() => {});
    axios.get(`${API}/admin/careers/applications`).then((r) => setApps(r.data)).catch(() => {});
  };
  useEffect(load, []);
  const toggle = async (t) => {
    await axios.patch(`${API}/admin/support/tickets/${t.id}`, { status: t.status === "open" ? "closed" : "open" });
    load();
  };
  const open = tickets.filter((t) => t.status === "open").length;
  return (
    <div data-testid="admin-inbox" className="grid lg:grid-cols-2 gap-6">
      <section className="space-y-3">
        <h3 className="font-ui text-sm font-semibold text-slate-200 flex items-center gap-2"><Inbox className="w-4 h-4" /> Support tickets <span className="text-slate-500">({open} open)</span></h3>
        {tickets.length ? tickets.map((t) => <Ticket key={t.id} t={t} onToggle={toggle} />) : <p className="font-ui text-sm text-slate-500">No tickets yet.</p>}
      </section>
      <section className="space-y-3">
        <h3 className="font-ui text-sm font-semibold text-slate-200 flex items-center gap-2"><Briefcase className="w-4 h-4" /> Job applications <span className="text-slate-500">({apps.length})</span></h3>
        {apps.length ? apps.map((a) => <Application key={a.id} a={a} />) : <p className="font-ui text-sm text-slate-500">No applications yet.</p>}
      </section>
    </div>
  );
};
